import { LoadingState } from "@repo/app-types/client/client";
import type { Company } from "@repo/app-types/entities/company";
import {
  PermissionActions,
  PermissionSubjects,
} from "@repo/app-types/entities/teammatePermissions";
import { FeatureFlagKey } from "@repo/app-types/featureFlags/keys";
import { getCompanyHasBrandingAccess } from "@repo/app-types/helpers/billingFeatureGating";
import {
  getFaviconUrl,
  getLogoUrl,
} from "@repo/app-types/helpers/publicStorage";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { FileUpload } from "@repo/ui/Inputs/fileUpload";
import { showToast, ToastVariant } from "@repo/ui/toast/toast";
import { IconTrash } from "@tabler/icons-react";
import { type FC, useState } from "react";
import { getAxiosInstanceWithAuth } from "../../../api/axiosConfig.js";
import { useIsFeatureFlagEnabled } from "../../../helpers/featureFlags/hooks.js";
import { Can } from "../../../helpers/teammateAuthorizationContext.js";
import { useAppDispatch } from "../../../hooks/hook.js";
import { UpsellBanner } from "../../billing/upsellBanner.js";
import { setCompany } from "../../company/companySlice.js";

interface CompanyImageEditorProps {
  company: Company;
  type: "logo" | "favicon";
}

export const CompanyImageEditor: FC<CompanyImageEditorProps> = ({
  company,
  type,
}) => {
  const dispatch = useAppDispatch();
  const isTrainingEnabled = useIsFeatureFlagEnabled(FeatureFlagKey.Training);

  const [uploadLoadingState, setUploadLoadingState] = useState<LoadingState>(
    LoadingState.LOADED,
  );
  const [deleteLoadingState, setDeleteLoadingState] = useState<LoadingState>(
    LoadingState.LOADED,
  );

  const imageUrl =
    type === "logo"
      ? company.settings.logo_file_name
        ? getLogoUrl(
            import.meta.env.VITE_SUPABASE_APP_URL,
            company.settings.logo_file_name,
          )
        : null
      : company.settings.favicon_file_name
        ? getFaviconUrl(
            import.meta.env.VITE_SUPABASE_APP_URL,
            company.settings.favicon_file_name,
          )
        : null;

  const onFileUpload = async (file: File) => {
    setUploadLoadingState(LoadingState.LOADING);
    const formData = new FormData();
    formData.append(type, file);

    try {
      const axios = await getAxiosInstanceWithAuth();

      // Remove the existing image if necessary
      if (imageUrl) {
        await axios.delete(`/company/${type}`);
      }

      const { data } = await axios.post(`/company/${type}`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      setUploadLoadingState(LoadingState.LOADED);
      dispatch(
        setCompany({
          ...company,
          settings: {
            ...company.settings,
            [type === "logo" ? "logo_file_name" : "favicon_file_name"]:
              data[`${type}_file_name`],
          },
        }),
      );
      return data;
    } catch (error) {
      showToast({
        title: `An error occurred - please try again`,
        variant: ToastVariant.Error,
      });

      setUploadLoadingState(LoadingState.ERROR);
    }
  };

  const onRemoveImage = async () => {
    setDeleteLoadingState(LoadingState.LOADING);

    try {
      const axios = await getAxiosInstanceWithAuth();
      await axios.delete(`/company/${type}`);
      setDeleteLoadingState(LoadingState.LOADED);
      dispatch(
        setCompany({
          ...company,
          settings: {
            ...company.settings,
            [type === "logo" ? "logo_file_name" : "favicon_file_name"]: null,
          },
        }),
      );
    } catch (error) {
      showToast({
        title: `An error occurred - please try again`,
        variant: ToastVariant.Error,
      });

      setDeleteLoadingState(LoadingState.ERROR);
    }
  };

  const isMissingFeatureAccess = !getCompanyHasBrandingAccess(company);
  const logoDisplayContext = isTrainingEnabled ? "trainings" : "interviews";

  return (
    <div>
      {imageUrl ? (
        <div className="my-2">
          <img
            src={imageUrl}
            alt={`Company ${type}`}
            className={
              type === "logo"
                ? "max-h-[50px] max-w-[150px]"
                : "max-h-[32px] max-w-[32px]"
            }
          />
        </div>
      ) : null}
      <div className="mt-2 flex flex-row gap-3">
        <Can
          I={PermissionActions.UPDATE}
          a={PermissionSubjects.COMPANY_SETTINGS}
          passThrough={true}
        >
          {(allowed) => (
            <>
              <FileUpload
                onUpload={onFileUpload}
                isLoading={uploadLoadingState === LoadingState.LOADING}
                label={imageUrl ? `Change ${type}` : `Upload ${type}`}
                isDisabled={isMissingFeatureAccess || !allowed}
              />
              {imageUrl ? (
                <Button
                  icon={<IconTrash className="h-4 w-4" />}
                  variant={ButtonVariant.Warning}
                  onClick={onRemoveImage}
                  isLoading={deleteLoadingState === LoadingState.LOADING}
                >
                  Remove {type}
                </Button>
              ) : null}
            </>
          )}
        </Can>
      </div>
      <div className="mt-2 text-sm text-muted-foreground">
        {type === "logo"
          ? `This logo (.png or .jpeg) will be displayed on the welcome screen for ${logoDisplayContext}. We recommend an image width of at least 100px and aspect ratio between 1:1 to 4:1.`
          : "This favicon (.png or .jpeg) will be displayed in the browser tab. We recommend a square image with dimensions of 16x16, 32x32, or 64x64 pixels."}
      </div>
      {isMissingFeatureAccess ? (
        <div className="mt-2">
          <UpsellBanner planFeatureName="whitelabeling" />
        </div>
      ) : null}
    </div>
  );
};
